import {
    IonContent, IonHeader, IonPage,
    IonTitle, IonToolbar, IonButtons, IonMenuButton,
    IonLabel, IonItem, IonModal, IonIcon, IonNote,
    IonInfiniteScroll, IonInfiniteScrollContent
} from '@ionic/react';
import { useEffect, useState } from 'react';
import CustomSearchBox from '../components/CustomSearchBox/CustomSearchBox';
import LoadingSkeleton from '../components/LoadingSkeleton';
import Plots from '../components/Plots';
import useDebounce from '../hooks/useDebounce';
import { getExpiredPlotsList, getExpiredPlotsCount } from '../services/plot.service';
import { funnelOutline } from 'ionicons/icons';
import PlotFilter from '../components/PlotFilter';
import { userProfileState } from '../services/state.service';
import { useRecoilValue } from 'recoil';

const LIMIT = 20;

const ExpiredPlots: React.FC = () => {
    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const userProfile: any = useRecoilValue(userProfileState);

    const [showFilterModal, setShowFilterModal] = useState(false);
    const [filterData, setFilterData] = useState<any>();
    const [filterCriteria, setFilterCriteria] = useState<any>(null);
    const [plots, setPlots] = useState([] as any);
    const [skip, setSkip] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    const debounceSearchText = useDebounce(searchText, 1000);

    // Fetch initial data when search or filter changes
    useEffect(() => {
        let mounted = true;
        setLoading(true);
        setSkip(0);
        setHasMore(true);

        // Get count for display (reflects applied search and filters)
        getExpiredPlotsCount(userProfile, debounceSearchText, filterCriteria).then(count => {
            if (mounted) setTotalCount(count);
        });

        // Get initial list with search and filter criteria
        getExpiredPlotsList(userProfile, 0, LIMIT, debounceSearchText, filterCriteria)
            .then(items => {
                if (mounted) {
                    setPlots(items);
                    setHasMore(items.length === LIMIT);
                    setLoading(false);
                }
            })
            .catch(() => {
                if (mounted) {
                    setPlots([]);
                    setLoading(false);
                }
            });

        return () => { mounted = false };
    }, [userProfile, debounceSearchText, filterCriteria]);

    // Load more for infinite scroll
    const loadMore = async (event: CustomEvent<void>) => {
        const newSkip = skip + LIMIT;
        const newItems = await getExpiredPlotsList(
            userProfile,
            newSkip,
            LIMIT,
            debounceSearchText,
            filterCriteria
        );

        setPlots((prev: any[]) => [...prev, ...newItems]);
        setSkip(newSkip);
        setHasMore(newItems.length === LIMIT);

        (event.target as HTMLIonInfiniteScrollElement).complete();
    };

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonMenuButton />
                    </IonButtons>
                    <IonTitle>Expired Plots</IonTitle>
                </IonToolbar>
            </IonHeader>
            <IonContent>
                <div className="searchBarContainer">
                    <div className="sbContainer">
                        <CustomSearchBox setSearchText={setSearchText} />
                    </div>
                    <div className="iconContainer">
                        <IonIcon
                            icon={funnelOutline}
                            slot="end"
                            onClick={(e) => setShowFilterModal(true)}
                        ></IonIcon>
                    </div>
                </div>

                {!loading && <IonItem lines="none">
                    <IonNote slot="end">{plots?.length} of {totalCount} expired plots</IonNote>
                </IonItem>}

                {loading && <LoadingSkeleton />}
                {!loading && (plots?.length > 0 ? <Plots plots={plots} /> : <IonItem><IonLabel>No plots found</IonLabel></IonItem>)}

                <IonInfiniteScroll
                    disabled={!hasMore}
                    onIonInfinite={loadMore}
                >
                    <IonInfiniteScrollContent
                        loadingSpinner="bubbles"
                        loadingText="Loading more plots..."
                    />
                </IonInfiniteScroll>

                <IonModal isOpen={showFilterModal} cssClass="my-custom-class">
                    <PlotFilter
                        filter={filterData}
                        filterChangeHandler={setFilterData}
                        onDoneHandler={setFilterCriteria}
                        onCloseHandler={setShowFilterModal}
                        hideFilters={{
                            crop: true,
                            tag: true
                        }}
                    ></PlotFilter>
                </IonModal>
            </IonContent>
        </IonPage>
    );
};

export default ExpiredPlots;

